// src/routes/financial.tsx
import React from "react";
import { Route } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";
import { EEntity, EModule } from "@/components/table/filter/useTables";

const PageCustomersSuppliers = React.lazy(() => import("@/modules/financial/pages/customersSuppliers").then(m => ({ default: m.PageCustomersSuppliers })));
const PageCustomerSupplier = React.lazy(() => import("@/modules/financial/pages/customersSuppliers/id").then(m => ({ default: m.PageCustomerSupplier })));

const PageCashAccounts = React.lazy(() => import("@/modules/financial/pages/cashAccounts").then(m => ({ default: m.PageCashAccounts })));
const PageCashAccount = React.lazy(() => import("@/modules/financial/pages/cashAccounts/id"));

const PageEntries = React.lazy(() => import("@/modules/financial/pages/entries").then(m => ({ default: m.PageEntries })));
const PageEntry = React.lazy(() => import("@/modules/financial/pages/entries/id"));

const PageInvoices = React.lazy(() => import("@/modules/financial/pages/invoices").then(m => ({ default: m.PageInvoices })));
const PageInvoice = React.lazy(() => import("@/modules/financial/pages/invoices/id"));

const PageEntryLows = React.lazy(() => import("@/modules/financial/pages/entryLows").then(m => ({ default: m.PageEntryLows })));
const PageEntryLow = React.lazy(() => import("@/modules/financial/pages/entryLows/id"));

const PageFinancialDashboards = React.lazy(() => import("@/modules/financial/pages/dashboard").then(m => ({ default: m.PageFinancialDashboards })));
const PageFinancialDashboard = React.lazy(() => import("@/modules/financial/pages/dashboard/id").then(m => ({ default: m.PageFinancialDashboard })));
import { Home } from "@mui/icons-material";
const FinancialDashboard = React.lazy(() => import("@/modules/financial/pages/home"));

const PagePaymentTerms = React.lazy(() => import("@/modules/financial/pages/paymentTerms"));
const PageReleaseReconciliation = React.lazy(() => import("@/modules/financial/pages/releaseReconciliation/id"));

const CostCenterModulePage = React.lazy(() => import("@/modules/financial/pages/costCenters/CostCenterModulePage"));

const PageEntryPaymentTerms = React.lazy(() => import("@/modules/financial/pages/entryPaymentTerms").then(m => ({ default: m.PageEntryPaymentTerms })));
const PageManagerialCategories = React.lazy(() => import("@/modules/financial/pages/managerialCategories").then(m => ({ default: m.PageManagerialCategories })));
const PageTeste = React.lazy(() => import("@/modules/pages"));

// ----------------------------------------------
// Helper: embrulha no ProtectedRoute (FINANCIAL)
// ----------------------------------------------
type Def = {
  path: string;
  element: React.ReactElement;
  auth: boolean;
  entity?: EEntity;
};

const wrap = ({ auth, entity, element }: Omit<Def, "path">) => (
  <React.Suspense fallback={<>Carregando...</>}>
    <ProtectedRoute
      auth={auth}
      {...(auth && entity ? { module: EModule.FINANCIAL, entity } : {})}
    >
      {element}
    </ProtectedRoute>
  </React.Suspense>
);

// Definições
// ----------------------------------------------
const customersSuppliers: Def[] = [
  {
    path: "/financial/customers_suppliers",
    auth: true,
    entity: EEntity.FIN_CASHACCOUNTS,
    element: <PageCustomersSuppliers />,
  },
  {
    path: "/financial/customers_suppliers/:idCustomerSupplier",
    auth: true,
    entity: EEntity.FIN_CASHACCOUNTS,
    element: <PageCustomerSupplier />,
  },
];

const cashAccounts: Def[] = [
  {
    path: "/financial/cash_accounts",
    auth: true,
    entity: EEntity.FIN_CASHACCOUNTS,
    element: <PageCashAccounts />,
  },
  {
    path: "/financial/cash_accounts/:idCashAccount",
    auth: true,
    entity: EEntity.FIN_CASHACCOUNTS,
    element: <PageCashAccount />,
  },
];

const entries: Def[] = [
  {
    path: "/financial/entries",
    auth: true,
    entity: EEntity.FIN_ENTRIES,
    element: <PageEntries />,
  },
  {
    path: "/financial/entries/receivable",
    auth: true,
    entity: EEntity.FIN_ENTRIES,
    element: <PageEntries />,
  },
  {
    path: "/financial/entries/payable",
    auth: true,
    entity: EEntity.FIN_ENTRIES,
    element: <PageEntries />,
  },
  {
    path: "/financial/entries/all",
    auth: true,
    entity: EEntity.FIN_ENTRIES,
    element: <PageEntries />,
  },
  {
    path: "/financial/entries/:idEntry/files/:idEntryFile",
    auth: true,
    entity: EEntity.FIN_ENTRIES,
    element: <PageEntry />,
  },
  {
    path: "/financial/entries/:idEntry",
    auth: true,
    entity: EEntity.FIN_ENTRIES,
    element: <PageEntry />,
  },
  {
    path: "/financial/entries/:idEntry/entry_lows/:idEntryLow",
    auth: true,
    entity: EEntity.FIN_ENTRYLOWS,
    element: <PageEntryLow />,
  },
  {
    path: "/financial/managerialCategory",
    auth: true,
    entity: EEntity.FIN_CATEGORIES,
    element: <PageManagerialCategories />,
  },
];

const invoices: Def[] = [
  {
    path: "/financial/invoices",
    auth: true,
    entity: EEntity.FIN_INVOICES,
    element: <PageInvoices />,
  },
  {
    path: "/financial/invoices/:idInvoice",
    auth: true,
    entity: EEntity.FIN_INVOICES,
    element: <PageInvoice />,
  },
];

const entryLows: Def[] = [
  {
    path: "/financial/entry_lows",
    auth: true,
    entity: EEntity.FIN_ENTRYLOWS,
    element: <PageEntryLows />,
  },
  {
    path: "/financial",
    auth: true,
    entity: EEntity.FIN_ENTRYLOWS,
    element: <FinancialDashboard />,
  },
  {
    path: "/financial/entry_lows/:idEntryLow",
    auth: true,
    entity: EEntity.FIN_ENTRYLOWS,
    element: <PageEntryLow />,
  },
];

const dashboards: Def[] = [
  {
    path: "/financial/dashboards",
    auth: false,
    element: <PageFinancialDashboards />,
  },
  {
    path: "/financial/dashboards/:idDashboard",
    auth: false,
    element: <PageFinancialDashboard />,
  },
];

const paymentTerms: Def[] = [
  {
    path: "/financial/payment-terms",
    auth: true,
    entity: EEntity.FIN_PAYMENTTERMS,
    element: <PagePaymentTerms />,
  },
];

const entryPaymentTerms: Def[] = [
  {
    path: "/financial/entry-payment-terms",
    auth: true,
    entity: EEntity.FIN_ENTRYPAYMENTTERMS,
    element: <PageEntryPaymentTerms />,
  },
];

const releaseReconciliation: Def[] = [
  {
    path: "/financial/release-reconciliation",
    auth: true,
    entity: EEntity.FIN_INVOICES,
    element: <PageReleaseReconciliation />,
  },
];

const costCenters: Def[] = [
  {
    path: "/financial/cost-centers",
    auth: true,
    entity: EEntity.FIN_COSTCENTERS,
    element: <CostCenterModulePage />,
  },
];

// Exports
// ----------------------------------------------
const defs: Def[] = [
  ...customersSuppliers,
  ...cashAccounts,
  ...entries,
  ...invoices,
  ...entryLows,
  ...dashboards,
  ...paymentTerms,
  ...entryPaymentTerms,
  ...releaseReconciliation,
  ...costCenters,
];

export const financialRoutes = defs.map(({ path, ...rest }) => (
  <Route key={path} path={path} element={wrap(rest)} />
));
